import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/lib/constants";
import { FOOTER } from "@/constants/testIds";

const linkCls =
  "text-sm text-[rgba(255,255,255,0.72)] hover:text-white transition-colors";

const Footer = () => {
  const { t, lang } = useLang();
  const year =
    lang === "ne"
      ? String(new Date().getFullYear()).replace(/\d/g, (d) => "०१२३४५६७८९"[d])
      : new Date().getFullYear();

  const company = [
    { to: "/about", label: t("footer.about"), id: FOOTER.about },
    { to: "/faq", label: t("footer.faq"), id: FOOTER.faq },
    { to: "/contact", label: t("footer.contact"), id: FOOTER.contact },
  ];
  const legal = [
    { to: "/privacy", label: t("footer.privacy"), id: FOOTER.privacy },
    { to: "/terms", label: t("footer.terms"), id: FOOTER.terms },
  ];
  const quick = [
    { to: "/market", label: t("nav.market"), id: FOOTER.market },
    { to: "/categories", label: t("nav.categories"), id: FOOTER.categories },
  ];

  const Col = ({ title, items }) => (
    <div>
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((i) => (
          <li key={i.to}>
            <Link to={i.to} data-testid={i.id} className={linkCls}>
              {i.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer data-testid={FOOTER.root} className="mt-12 bg-[#123A2C] text-white">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="font-extrabold text-lg">{t("app.name")}</div>
            <p className="mt-2 text-sm text-[rgba(255,255,255,0.72)] leading-relaxed max-w-xs">
              {t("footer.desc")}
            </p>
          </div>
          <Col title={t("footer.company")} items={company} />
          <Col title={t("footer.support")} items={legal} />
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-sm font-semibold text-white mb-3">
              {t("footer.contactTitle")}
            </h3>
            <ul className="space-y-2 text-sm text-[rgba(255,255,255,0.72)]">
              {CONTACT.email && (
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 shrink-0" />
                  <a
                    data-testid={FOOTER.email}
                    href={`mailto:${CONTACT.email}`}
                    className="hover:text-white break-all"
                  >
                    {CONTACT.email}
                  </a>
                </li>
              )}
              {CONTACT.phone && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" />
                  <a
                    data-testid={FOOTER.phone}
                    href={`tel:${CONTACT.phone}`}
                    className="hover:text-white"
                  >
                    {CONTACT.phone}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 shrink-0" />
                {t("footer.address")}
              </li>
            </ul>
            <div className="mt-4 flex gap-4">
              {quick.map((i) => (
                <Link
                  key={i.to}
                  to={i.to}
                  data-testid={i.id}
                  className={linkCls}
                >
                  {i.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 pt-5 border-t border-[rgba(255,255,255,0.15)] flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-xs text-[rgba(255,255,255,0.6)]">
          <span>
            © {year} {t("app.name")}. {t("footer.rights")}
          </span>
          <span>{t("footer.madeIn")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
